import { useState, useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Search, Users, UserCheck, Calendar, UserMinus, Trash2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useCompanyEmployees, useLeavePolicyMutations, usePolicyAssignments } from '@/hooks/leave';
import { leavePolicyService } from '@/services/leave/leavePolicyService';
import { useQuery } from '@tanstack/react-query';
import { useToast } from '@/hooks/use-toast';
import type { Profile } from '@/services/leave/types';

interface EmployeeAssignmentDialogProps {
  policyId: string | null;
  policyName: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode?: 'assign' | 'manage'; // New mode prop
}

export function EmployeeAssignmentDialog({ 
  policyId, 
  policyName, 
  open, 
  onOpenChange,
  mode = 'assign' // Default to assign mode
}: EmployeeAssignmentDialogProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedEmployees, setSelectedEmployees] = useState<string[]>([]);
  const [selectedAssignments, setSelectedAssignments] = useState<string[]>([]);
  const [isAssigning, setIsAssigning] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);
  const [activeTab, setActiveTab] = useState<string>(mode);

  const { data: employees, isLoading: loadingEmployees } = useCompanyEmployees();
  const { data: assignments, isLoading: loadingAssignments } = usePolicyAssignments(policyId || '');
  const { assignPolicyToEmployees, removePolicyAssignments } = useLeavePolicyMutations();
  const { toast } = useToast();

  // Get assignments with profile data for manage mode
  const { data: assignmentsWithProfiles, isLoading: loadingAssignmentsWithProfiles } = useQuery({
    queryKey: ['assignmentsWithProfiles', policyId],
    queryFn: () => policyId ? leavePolicyService.getAssignmentsWithProfiles(policyId) : Promise.resolve([]),
    enabled: !!policyId && activeTab === 'manage',
    staleTime: 2 * 60 * 1000,
  });

  // Filter employees based on search term
  const filteredEmployees = useMemo(() => {
    if (!employees) return [];
    
    const searchLower = searchTerm.toLowerCase();
    return employees.filter(employee => 
      (employee.first_name?.toLowerCase().includes(searchLower) || '') ||
      (employee.last_name?.toLowerCase().includes(searchLower) || '') ||
      employee.email.toLowerCase().includes(searchLower)
    );
  }, [employees, searchTerm]);

  // Filter assignments based on search term
  const filteredAssignments = useMemo(() => {
    if (!assignmentsWithProfiles) return [];
    
    const searchLower = searchTerm.toLowerCase();
    return assignmentsWithProfiles.filter(assignment => 
      (assignment.profile.first_name?.toLowerCase().includes(searchLower) || '') ||
      (assignment.profile.last_name?.toLowerCase().includes(searchLower) || '') ||
      assignment.profile.email.toLowerCase().includes(searchLower)
    );
  }, [assignmentsWithProfiles, searchTerm]);

  // Get currently assigned employee IDs
  const assignedEmployeeIds = useMemo(() => 
    new Set(assignments?.map(a => a.profile_id) || []), 
    [assignments]
  );

  const handleEmployeeToggle = (profileId: string, isChecked: boolean) => {
    if (isChecked) {
      setSelectedEmployees(prev => [...prev, profileId]);
    } else {
      setSelectedEmployees(prev => prev.filter(id => id !== profileId));
    }
  };

  const handleAssignmentToggle = (assignmentId: string, isChecked: boolean) => {
    if (isChecked) {
      setSelectedAssignments(prev => [...prev, assignmentId]);
    } else {
      setSelectedAssignments(prev => prev.filter(id => id !== assignmentId));
    }
  };

  const handleAssignSelected = async () => {
    if (!policyId || selectedEmployees.length === 0) return;

    setIsAssigning(true);
    try {
      await assignPolicyToEmployees.mutateAsync({
        policy_id: policyId,
        profile_ids: selectedEmployees,
      });
      setSelectedEmployees([]);
      toast({
        title: "Success",
        description: `Assigned ${selectedEmployees.length} employee(s) to ${policyName}`,
      });
    } catch (error) {
      console.error('Failed to assign employees:', error);
    } finally {
      setIsAssigning(false);
    }
  };

  const handleRemoveSelected = async () => {
    if (!policyId || selectedAssignments.length === 0) return;

    setIsRemoving(true);
    try {
      await removePolicyAssignments.mutateAsync(selectedAssignments);
      setSelectedAssignments([]);
      toast({
        title: "Success",
        description: `Removed ${selectedAssignments.length} assignment(s) from ${policyName}`,
      });
    } catch (error) {
      console.error('Failed to remove assignments:', error);
    } finally {
      setIsRemoving(false);
    }
  };

  const getEmployeeDisplayName = (employee: Profile) => {
    if (employee.first_name || employee.last_name) {
      return `${employee.first_name || ''} ${employee.last_name || ''}`.trim();
    }
    return employee.email;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  if (!policyId) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh] flex flex-col">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            Manage Employees - {policyName}
          </DialogTitle>
          <DialogDescription>
            Assign new employees to this policy or manage existing assignments.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-4 flex-1 min-h-0">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="flex flex-col flex-1 min-h-0">
            <TabsList className="grid w-full grid-cols-2 flex-shrink-0">
              <TabsTrigger value="assign">Assign New Employees</TabsTrigger>
              <TabsTrigger value="manage">Manage Assignments</TabsTrigger>
            </TabsList>

            <TabsContent value="assign" className="flex-1 min-h-0 flex flex-col gap-4">
              {/* Search */}
              <div className="relative flex-shrink-0">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
                <Input
                  placeholder="Search employees by name or email..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>

              {/* Assignment Stats */}
              <div className="grid grid-cols-2 gap-2 flex-shrink-0">
                <Card className="py-1">
                  <CardContent className="pt-2 pb-1 px-3">
                    <div className="flex items-center gap-1.5">
                      <UserCheck className="h-3 w-3 text-green-600" />
                      <div>
                        <p className="text-xs text-muted-foreground">Currently Assigned</p>
                        <p className="text-base font-semibold">
                          {loadingAssignments ? '...' : assignedEmployeeIds.size}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card className="py-1">
                  <CardContent className="pt-2 pb-1 px-3">
                    <div className="flex items-center gap-1.5">
                      <Calendar className="h-3 w-3 text-blue-600" />
                      <div>
                        <p className="text-xs text-muted-foreground">Selected to Assign</p>
                        <p className="text-base font-semibold">{selectedEmployees.length}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Employee List */}
              <ScrollArea className="h-[320px] border rounded-md">
                <div className="p-4 space-y-2">
                  {loadingEmployees ? (
                    <div className="text-center py-8 text-muted-foreground">
                      Loading employees...
                    </div>
                  ) : filteredEmployees.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">
                      {searchTerm ? 'No employees found matching your search.' : 'No employees available.'}
                    </div>
                  ) : (
                    filteredEmployees.map((employee) => {
                      const isAssigned = assignedEmployeeIds.has(employee.id);
                      const isSelected = selectedEmployees.includes(employee.id);
                      
                      return (
                        <div
                          key={employee.id}
                          className={`flex items-center space-x-3 p-3 rounded-lg border ${
                            isAssigned ? 'bg-green-50 border-green-200' : 'hover:bg-muted/50'
                          }`}
                        >
                          <Checkbox
                            id={employee.id}
                            checked={isSelected}
                            disabled={isAssigned}
                            onCheckedChange={(checked) => 
                              handleEmployeeToggle(employee.id, checked as boolean)
                            }
                          />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <p className="font-medium">
                                {getEmployeeDisplayName(employee)}
                              </p>
                              {isAssigned && (
                                <Badge variant="secondary" className="text-xs">
                                  Already Assigned
                                </Badge>
                              )}
                            </div>
                            <p className="text-sm text-muted-foreground">
                              {employee.email}
                            </p>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </ScrollArea>

              {/* Actions */}
              <div className="flex justify-between items-center pt-4 border-t flex-shrink-0">
                <div className="text-sm text-muted-foreground">
                  {selectedEmployees.length > 0 && (
                    <span>{selectedEmployees.length} employee(s) selected</span>
                  )}
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => onOpenChange(false)}>
                    Cancel
                  </Button>
                  <Button 
                    onClick={handleAssignSelected}
                    disabled={selectedEmployees.length === 0 || isAssigning}
                  >
                    {isAssigning ? 'Assigning...' : `Assign ${selectedEmployees.length} Employee(s)`}
                  </Button>
                </div>
              </div>
            </TabsContent>

            <TabsContent value="manage" className="flex-1 min-h-0 flex flex-col gap-4">
              {/* Search */}
              <div className="relative flex-shrink-0">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
                <Input
                  placeholder="Search assigned employees..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>

              {/* Assignment Stats */}
              <div className="grid grid-cols-2 gap-2 flex-shrink-0">
                <Card className="py-1">
                  <CardContent className="pt-2 pb-1 px-3">
                    <div className="flex items-center gap-1.5">
                      <Users className="h-3 w-3 text-blue-600" />
                      <div>
                        <p className="text-xs text-muted-foreground">Total Assigned</p>
                        <p className="text-base font-semibold">
                          {loadingAssignmentsWithProfiles ? '...' : (assignmentsWithProfiles?.length || 0)}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card className="py-1">
                  <CardContent className="pt-2 pb-1 px-3">
                    <div className="flex items-center gap-1.5">
                      <UserMinus className="h-3 w-3 text-red-600" />
                      <div>
                        <p className="text-xs text-muted-foreground">Selected to Remove</p>
                        <p className="text-base font-semibold">{selectedAssignments.length}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Assignments List */}
              <ScrollArea className="h-[320px] border rounded-md">
                <div className="p-4 space-y-2">
                  {loadingAssignmentsWithProfiles ? (
                    <div className="text-center py-8 text-muted-foreground">
                      Loading assignments...
                    </div>
                  ) : filteredAssignments.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">
                      {searchTerm ? 'No assignments found matching your search.' : 'No employees are currently assigned to this policy.'}
                    </div>
                  ) : (
                    filteredAssignments.map((assignment) => {
                      const isSelected = selectedAssignments.includes(assignment.id);
                      
                      return (
                        <div
                          key={assignment.id}
                          className="flex items-center space-x-3 p-3 rounded-lg border hover:bg-muted/50"
                        >
                          <Checkbox
                            id={assignment.id}
                            checked={isSelected}
                            onCheckedChange={(checked) => 
                              handleAssignmentToggle(assignment.id, checked as boolean)
                            }
                          />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <p className="font-medium">
                                {getEmployeeDisplayName(assignment.profile)}
                              </p>
                              <Badge variant="secondary" className="text-xs">
                                Assigned {formatDate(assignment.start_date)}
                              </Badge>
                            </div>
                            <p className="text-sm text-muted-foreground">
                              {assignment.profile.email}
                            </p>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </ScrollArea>

              {/* Actions */}
              <div className="flex justify-between items-center pt-4 border-t flex-shrink-0">
                <div className="text-sm text-muted-foreground">
                  {selectedAssignments.length > 0 && (
                    <span>{selectedAssignments.length} assignment(s) selected</span>
                  )}
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => onOpenChange(false)}>
                    Cancel
                  </Button>
                  <Button 
                    variant="destructive"
                    onClick={handleRemoveSelected}
                    disabled={selectedAssignments.length === 0 || isRemoving}
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    {isRemoving ? 'Removing...' : `Remove ${selectedAssignments.length} Assignment(s)`}
                  </Button>
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </DialogContent>
    </Dialog>
  );
}